import { useEffect, useState } from "react";
import { subscribeToRecoveryAcrossProviders } from "@/integrations/supabase/authProviders";
import { clearRecoveryLanding, peekRecoveryLanding } from "@/lib/recoveryLanding";

/** Where a password-recovery link must end up, whatever path it arrived on. */
export const RESET_PASSWORD_PATH = "/reset-password";

/**
 * True while a password-recovery landing still needs to be routed to
 * {@link RESET_PASSWORD_PATH}. See `src/lib/recoveryLanding.ts` for the bug
 * this exists to prevent.
 *
 * Two independent triggers, because neither alone is sufficient:
 *
 *  1. The module-load fragment capture — synchronous, so it cannot lose the
 *     race against supabase-js clearing the URL. This is the one that fires in
 *     practice.
 *  2. The PASSWORD_RECOVERY auth event — asynchronous, and covers the case
 *     where the recovery marker never reaches the fragment we captured.
 *
 * The initial read happens in a lazy useState initializer rather than an
 * effect, so the answer is available during the FIRST render. That matters:
 * the caller has to be able to short-circuit before <ProtectedRoute> mounts
 * and queues its own redirect. An effect-based check would run after those
 * guards had already committed, leaving two `replace` navigations racing.
 *
 * @param pathname current router pathname, so the flag can release itself on
 *        arrival (see below).
 */
export function useRecoveryLanding(pathname: string): boolean {
  // peek, not consume — StrictMode double-invokes this initializer in dev, and
  // a destructive read would return false the second time. See recoveryLanding.ts.
  const [recovering, setRecovering] = useState(peekRecoveryLanding);

  useEffect(() => {
    // The module flag has done its job once it has seeded React state; clearing
    // here keeps a later remount from re-triggering an already-handled landing.
    clearRecoveryLanding();

    return subscribeToRecoveryAcrossProviders(() => setRecovering(true));
  }, []);

  // Release once we have arrived. The flag's only job is to get the operator
  // TO the reset page; holding it after that would trap them there, because
  // ResetPassword navigates away on success (to `/`, which then routes to the
  // dashboard their org runs).
  useEffect(() => {
    if (recovering && pathname === RESET_PASSWORD_PATH) setRecovering(false);
  }, [recovering, pathname]);

  return recovering;
}
